import { Component, computed, DestroyRef, inject, input, model, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormValueControl } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatError } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { DictionaryDto } from '@app/shared/services/dtos/dictionary.dto';
import { LookupService } from '@app/shared/services/lookup.service';
import { proffeoUiFormErrorMessage, ProffeoUiFormErrors } from '@app/shared/ui/forms/ui-form-control';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { ProffeoUiInputValue } from '@app/shared/ui/types/input.types';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * OSH dictionary picker (work type, possible threat …) with an inline "add new entry" row.
 *
 * @summary `FormValueControl<DictionaryDto | null>` — bind with Signal Forms `[formField]`. The value is always a
 * plain `DictionaryDto` copy; a free-text entry is created in the dictionary and selected as the created DTO.
 */
@Component({
  selector: 'proffeo-osh-analysis-dictionary-dropdown',
  imports: [MatSelectModule, MatCheckboxModule, MatError, UiInputComponent, TranslatePipe, MatButtonModule],
  templateUrl: './osh-analysis-dictionary-dropdown.component.html'
})
export class OshAnalysisDictionaryDropdownComponent implements FormValueControl<DictionaryDto | null> {
  private readonly lookupService: LookupService = inject(LookupService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly select = viewChild.required<MatSelect>('select');

  protected readonly isNewDictionaryItem = signal(false);
  protected readonly newOption = signal('');

  protected readonly optionItems = computed((): readonly DictionaryDto[] => this.options() ?? []);

  protected readonly showError = computed((): boolean => !this.disabled() && this.invalid() && this.touched());

  protected readonly errorText = computed((): string => proffeoUiFormErrorMessage('', this.errors()));

  /** Keeps the Material invalid styling of the select in sync with the field state. */
  protected readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: (): boolean => this.showError()
  };

  /** @summary Selected dictionary entry — bound by `[formField]`. */
  public readonly value = model<DictionaryDto | null>(null);

  public readonly options = input.required<readonly DictionaryDto[] | undefined>();
  public readonly label = input.required<string>();
  public readonly dictionaryType = input.required<string>();

  /** @summary Field state — bound by `[formField]`. */
  public readonly disabled = input<boolean>(false);
  public readonly invalid = input<boolean>(false);
  public readonly touched = input<boolean>(false);
  public readonly errors = input<ProffeoUiFormErrors>([]);

  /** @summary Emits when the panel closes — marks a `[formField]` as touched. */
  public readonly touch = output<void>();

  protected readonly compareFn = (o1: DictionaryDto, o2: DictionaryDto): boolean => !!o1 && !!o2 && o1.id === o2.id;

  protected onSelectionChange(selected: DictionaryDto | undefined): void {
    if (!selected) {
      // The "add new entry" row is a value-less option: keep the current selection.
      this.select().value = this.value();
      return;
    }
    this.value.set({ ...selected });
  }

  protected onOpenedChange(opened: boolean): void {
    if (!opened) {
      this.touch.emit();
    }
  }

  protected onNewDictionaryItemCheckboxChange(event: MatCheckboxChange): void {
    this.isNewDictionaryItem.set(event.checked);
  }

  protected onNewOptionChange(value: ProffeoUiInputValue): void {
    this.newOption.set(String(value ?? ''));
  }

  protected cancelNewOption(): void {
    this.newOption.set('');
    this.isNewDictionaryItem.set(false);
  }

  public addOption(): void {
    const trimmed = this.newOption().trim();
    if (!trimmed) {
      return;
    }

    this.cancelNewOption();
    const existing = this.optionItems().find(option => option.value === trimmed);
    if (existing) {
      this.value.set({ ...existing });
    } else {
      this.createOption(trimmed);
    }
    this.touch.emit();
    this.select().close();
  }

  /**
   * Selects a pending entry right away (no id yet) and swaps in the created DTO once the API returns it, unless
   * the user picked something else meanwhile. On failure the previous selection is restored.
   */
  private createOption(value: string): void {
    const previous = this.value();
    const pending: DictionaryDto = {
      id: null,
      companyId: this.activeCompanyService.activeCompany()?.id ?? null,
      type: this.dictionaryType(),
      value
    };
    const isStillPending = (): boolean => this.value()?.id === null && this.value()?.value === value;
    this.value.set(pending);

    this.lookupService
      .addItemToDictionary(this.dictionaryType(), { ...pending })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: created => {
          if (isStillPending()) {
            this.value.set({ ...created });
          }
        },
        error: () => {
          if (isStillPending()) {
            this.value.set(previous);
          }
        }
      });
  }
}
